import { computed, defineAsyncComponent, inject, shallowRef, type App, type Component, type ComputedRef, type InjectionKey } from 'vue';
import { useSession } from './stores/session';

/**
 * Navegação interna do jogo: uma pilha de telas em memória, sem `vue-router` e sem mudar a URL (ela fica sempre em `/`).
 * Uma tela comum cobre a anterior; uma tela `modal` (a mochila, o ferreiro) abre por cima e deixa a tela de baixo visível.
 * As regras de entrada (sessão, personagem criado, papel) valem em todo `push`, `replace` e `reset`. O servidor continua sendo quem autoriza de fato.
 */
export interface ScreenDef {
  component: Component;
  title: string;
  /** Exige sessão. */
  auth?: boolean;
  /** Exige papel gm ou admin. */
  gm?: boolean;
  /** Exige papel admin. */
  admin?: boolean;
  /** Só para quem NÃO entrou (login, cadastro): quem já está dentro vai para o salão. */
  guest?: boolean;
  /** Abre por cima da tela atual, sem escondê-la. */
  modal?: boolean;
  /** Endereço que abre esta tela quando o app é aberto por um link externo (ex.: o e-mail de nova senha). Só lido uma vez, na abertura. */
  entryPath?: string | string[];
}

const lazy = (load: () => Promise<{ default: Component }>) => defineAsyncComponent(load);

export const SCREENS: Record<string, ScreenDef> = {
  home: { component: lazy(() => import('./views/HomeView.vue')), title: 'Início' },
  login: { component: lazy(() => import('./views/LoginView.vue')), title: 'Entrar', guest: true },
  register: { component: lazy(() => import('./views/RegisterView.vue')), title: 'Criar conta', guest: true },
  forgot: { component: lazy(() => import('./views/ForgotView.vue')), title: 'Recuperar senha', guest: true },
  reset: { component: lazy(() => import('./views/ResetView.vue')), title: 'Nova senha', guest: true, entryPath: ['/reset', '/redefinir-senha'] },
  'create-character': { component: lazy(() => import('./views/CreateCharacterView.vue')), title: 'Criar personagem', auth: true },
  profile: { component: lazy(() => import('./views/ProfileView.vue')), title: 'Perfil', auth: true },
  forge: { component: lazy(() => import('./views/ForgeView.vue')), title: 'Ferreiro', auth: true, modal: true },
  shop: { component: lazy(() => import('./views/ShopView.vue')), title: 'Loja', auth: true },
  mail: { component: lazy(() => import('./views/MailView.vue')), title: 'Correio', auth: true, modal: true },
  quests: { component: lazy(() => import('./views/QuestView.vue')), title: 'Missões', auth: true, modal: true },
  hall: { component: lazy(() => import('./views/HallView.vue')), title: 'Salão das Fendas', auth: true },
  inventory: { component: lazy(() => import('./views/InventoryView.vue')), title: 'Inventário', auth: true, modal: true },
  pvp: { component: lazy(() => import('./views/PartyView.vue')), title: 'PvP', auth: true },
  friends: { component: lazy(() => import('./views/FriendsView.vue')), title: 'Amigos', auth: true, modal: true },
  versus: { component: lazy(() => import('./views/VersusView.vue')), title: 'Confronto', auth: true },
  match: { component: lazy(() => import('./views/MatchView.vue')), title: 'Partida', auth: true },
  // Só em desenvolvimento: as telas do cliente novo sem exigir login (para conferir o visual com a arte original).
  ...(import.meta.env.DEV
    ? {
        'dev-shop': { component: lazy(() => import('./views/DevShopView.vue')), title: 'Loja (dev)' },
        'dev-create': { component: lazy(() => import('./views/CreateCharacterView.vue')), title: 'Criar personagem (dev)' },
        'dev-mail': { component: lazy(() => import('./views/DevMailView.vue')), title: 'Correio (dev)' },
        'dev-bag': { component: lazy(() => import('./views/DevBagView.vue')), title: 'Mochila (dev)' },
        'dev-forge': { component: lazy(() => import('./views/DevForgeView.vue')), title: 'Ferreiro (dev)' },
        'dev-show-sheet': { component: lazy(() => import('./views/DevShowSheetView.vue')), title: 'Folha do boneco (dev)' },
        'dev-hall': { component: lazy(() => import('./views/HallView.vue')), title: 'Salão das Fendas (dev)' },
        'dev-pvp': { component: lazy(() => import('./views/PartyView.vue')), title: 'PvP (dev)' },
        'ui-catalog': { component: lazy(() => import('./views/UiCatalogView.vue')), title: 'Catálogo da interface' },
      }
    : {}),
};

export type Query = Record<string, string>;
/** O destino é o nome da tela, com parâmetros opcionais. */
export type Target = string | { name: string; query?: Query };

export interface Entry {
  /** Identifica a instância na pilha (a mesma tela pode aparecer duas vezes). */
  id: number;
  name: string;
  query: Query;
}

export const isScreen = (name: unknown): name is string => typeof name === 'string' && Object.hasOwn(SCREENS, name);

export interface Navigator {
  /** A pilha inteira, da base ao topo. */
  readonly stack: ComputedRef<readonly Entry[]>;
  /** A tela do topo. */
  readonly current: ComputedRef<Entry>;
  /** A tela de baixo que ocupa a janela inteira, com os modais abertos por cima dela. */
  readonly visible: ComputedRef<readonly Entry[]>;
  /** Abre a tela por cima da atual. Abrir a mesma tela que já está no topo só a recarrega. */
  push(target: Target): void;
  /** Troca a tela do topo. */
  replace(target: Target): void;
  /** Descarta a pilha e começa de novo na tela dada. */
  reset(target: Target): void;
  /** Fecha a tela do topo. Sem tela por baixo, vai para o salão. */
  back(): void;
  install(app: App): void;
}

const KEY: InjectionKey<Navigator> = Symbol('navigator');

/** Aplica as regras de entrada e devolve a tela que realmente abre. */
function resolve(target: Target): { name: string; query: Query } {
  const wanted = typeof target === 'string' ? { name: target, query: {} } : { name: target.name, query: target.query ?? {} };
  const name = isScreen(wanted.name) ? wanted.name : 'home';
  const def = SCREENS[name]!;
  const session = useSession();
  if (def.auth && !session.authenticated) return { name: 'login', query: { redirect: name } };
  // A entrada do jogo é o Salão das Fendas; quem não entrou vai ao login.
  if (name === 'home') return { name: session.authenticated ? 'hall' : 'login', query: {} };
  // Conta nova: antes de qualquer outra tela, o jogador escolhe nickname e sexo.
  if (session.needsCharacter && name !== 'create-character') return { name: 'create-character', query: {} };
  if (name === 'create-character' && session.authenticated && !session.needsCharacter) return { name: 'hall', query: {} };
  if (def.guest && session.authenticated) return { name: 'hall', query: {} };
  if ((def.gm && !session.isGm) || (def.admin && !session.isAdmin)) return { name: 'profile', query: {} };
  return { name, query: wanted.query };
}

let sequence = 0;
const entry = (r: { name: string; query: Query }): Entry => ({ id: ++sequence, name: r.name, query: r.query });

export function createNavigator(): Navigator {
  const stack = shallowRef<readonly Entry[]>([entry({ name: 'home', query: {} })]);
  const current = computed(() => stack.value[stack.value.length - 1]!);
  const visible = computed(() => {
    const all = stack.value;
    let base = all.length - 1;
    while (base > 0 && SCREENS[all[base]!.name]?.modal) base--;
    return all.slice(base);
  });

  const nav: Navigator = {
    stack: computed(() => stack.value),
    current,
    visible,
    push(target) {
      const next = resolve(target);
      if (next.name === current.value.name) stack.value = [...stack.value.slice(0, -1), entry(next)];
      else stack.value = [...stack.value, entry(next)];
    },
    replace(target) {
      stack.value = [...stack.value.slice(0, -1), entry(resolve(target))];
    },
    reset(target) {
      stack.value = [entry(resolve(target))];
    },
    back() {
      if (stack.value.length > 1) stack.value = stack.value.slice(0, -1);
      else nav.reset('hall');
    },
    install(app) {
      app.provide(KEY, nav);
    },
  };
  return nav;
}

export function useNav(): Navigator {
  const nav = inject(KEY);
  if (!nav) throw new Error('Navegador não instalado: use app.use(createNavigator()).');
  return nav;
}

/**
 * A abertura do app por um link externo (o e-mail de nova senha): lê o endereço uma só vez e devolve a tela e os parâmetros.
 * Em desenvolvimento aceita também `/dev/hall`, `/forge`... para abrir qualquer tela direto. Depois o chamador volta o endereço para `/`.
 */
export function entryFromLocation(path: string, search: string): Target | null {
  const clean = path.replace(/\/+$/, '') || '/';
  const query: Query = Object.fromEntries(new URLSearchParams(search));
  for (const [name, def] of Object.entries(SCREENS)) {
    const paths = def.entryPath ? [def.entryPath].flat() : [];
    if (import.meta.env.DEV) paths.push(`/${name.startsWith('dev-') ? `dev/${name.slice(4)}` : name}`);
    if (paths.includes(clean)) return { name, query };
  }
  return null;
}
